import {
  Box,
  Heading,
  Text,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link as ChakraLink,
  Divider,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import BasePageLayout from '@/components/layout/BasePageLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TroubleshootStep, type StepScreenshot, type ManualLink } from './HelpStepContent'

const H = (file: string) => `/assets/img/help/${file}`

type FaqItem = {
  question: string
  symptom: string
  causes: string[]
  steps: string[]
  screenshots?: StepScreenshot[]
  manualLinks?: ManualLink[]
}

type FaqCategory = {
  title: string
  intro?: string
  items: FaqItem[]
}

const faqCategories: FaqCategory[] = [
  {
    title: 'Sign-in, lockout, and two-factor authentication',
    intro:
      'Use this section when someone cannot sign in, is locked out, or keeps failing two-factor authentication (2FA).',
    items: [
      {
        question: 'Wrong password or account not found',
        symptom: 'Login shows invalid credentials or the user cannot remember their password.',
        causes: [
          'Incorrect username or password.',
          'Caps Lock or keyboard layout mismatch.',
        ],
        steps: [
          'Confirm the username and password on the login screen.',
          'Use the Forgot Password? link to request a reset email.',
          'If reset mail never arrives, see Email not delivering below.',
        ],
        screenshots: [
          {
            src: H('01-login.png'),
            alt: 'Login screen',
            caption: 'Step 1: Confirm credentials on the login screen.',
          },
          {
            src: H('02-forgot-password.png'),
            alt: 'Forgot password',
            caption: 'Step 2: Use Forgot Password? to start recovery.',
          },
        ],
        manualLinks: [{ label: 'Getting started → Sign in' }],
      },
      {
        question: 'Two-factor code keeps failing',
        symptom: 'The 6-digit verification code is rejected or expires before it can be entered.',
        causes: [
          'Device clock is wrong (TOTP codes are time-based).',
          'Too many failed attempts; code expired or was already used.',
        ],
        steps: [
          'Verify the device date and time are set automatically.',
          'On the Two-Factor Authentication screen, enter the latest code or use Resend code.',
          'If the user has backup codes, enter one instead of the email code.',
          'An administrator can reset 2FA from User Management → Users → Edit if access is permanently lost.',
        ],
        screenshots: [
          {
            src: H('03-two-factor.png'),
            alt: 'Two-factor authentication',
            caption: 'Step 2: Enter the verification code or resend a new one.',
          },
        ],
        manualLinks: [{ label: 'Getting started → Two-factor authentication' }],
      },
      {
        question: 'Account suspended, inactive, or soft-deleted',
        symptom: 'Valid credentials fail, or the user no longer appears in the Users list.',
        causes: [
          'User Status is Suspended or Inactive.',
          'The account was soft-deleted and moved to Trash.',
          'Security lockout after too many failed login attempts.',
        ],
        steps: [
          'Open User Management → Users → All and search for the account.',
          'If missing, switch to the Trash tab and Restore the user.',
          'Edit the user and set Status to Active if it was Suspended or Inactive.',
          'Review Settings → Security for lockout duration and max login attempts.',
        ],
        screenshots: [
          {
            src: H('faq-06-users-edit-fix-role.png'),
            alt: 'Edit user status and role',
            caption: 'Step 3: Edit user — verify Status, Role, and Client.',
          },
          {
            src: H('faq-01-users-trash-restore.png'),
            alt: 'Users Trash tab with Restore',
            caption: 'Step 2: Trash tab — Restore brings the record back to All; Delete Permanently cannot be undone in-app.',
          },
          {
            src: H('set-02-security.png'),
            alt: 'Security settings',
            caption: 'Step 4: Security Settings — lockout and session rules.',
          },
        ],
        manualLinks: [
          { label: 'User Management → Users' },
          { label: 'Settings → Security' },
        ],
      },
    ],
  },
  {
    title: 'Wrong or missing records (Trash / Restore)',
    intro:
      'Primary recovery path when a record was deleted by mistake or seems missing from a list.',
    items: [
      {
        question: 'A user or record disappeared from the list',
        symptom: 'A row that existed before is no longer visible on the All tab.',
        causes: [
          'The record was soft-deleted (moved to Trash).',
          'Search or filters hide the row.',
          'Wrong client or status filter applied.',
        ],
        steps: [
          'Open the list page and confirm you are on the All tab.',
          'Clear search and column filters.',
          'Switch to the Trash tab — if the row appears, select it and choose Bulk action Restore, then Apply.',
          'Never use Delete Permanently unless the data must be gone forever; that cannot be undone in-app.',
        ],
        screenshots: [
          {
            src: H('um-01-users.png'),
            alt: 'Users list All tab',
            caption: 'Step 1: All tab — clear search and filters first.',
          },
          {
            src: H('faq-01-users-trash-restore.png'),
            alt: 'Restore from Trash',
            caption: 'Step 3: Trash tab — Restore brings the record back to All; Delete Permanently (Force Delete) cannot be undone in-app.',
          },
        ],
        manualLinks: [{ label: 'User Management → Users' }],
      },
      {
        question: 'Wrong organization record was deleted',
        symptom: 'A client, store, or other org record is missing after someone clicked Delete.',
        causes: ['Soft-delete moved the record to Trash.', 'List was filtered by client or status.'],
        steps: [
          'Open Organization → the relevant list (e.g. Clients).',
          'Confirm All tab and clear filters.',
          'Open Trash, select the row(s), choose Restore from Bulk actions, and click Apply.',
          'Never use Delete Permanently unless the data must be gone forever; Force Delete cannot be undone in-app.',
        ],
        screenshots: [
          {
            src: H('faq-02-clients-trash-restore.png'),
            alt: 'Clients Trash tab',
            caption: 'Step 3: Clients Trash — Restore selected rows; Delete Permanently cannot be undone in-app.',
          },
        ],
        manualLinks: [{ label: 'Organization → Clients' }],
      },
      {
        question: 'Temporary access removal — prefer Suspend over Delete',
        symptom: 'You need to block a user temporarily without losing their history.',
        causes: ['Delete was used when Suspend or Inactive would have been enough.'],
        steps: [
          'For users, edit the account and set Status to Suspended or Inactive instead of deleting.',
          'If already deleted, restore from Trash first, then change Status.',
          'Reserve Delete for records that should leave the active list entirely.',
        ],
        screenshots: [
          {
            src: H('faq-06-users-edit-fix-role.png'),
            alt: 'Edit user status',
            caption: 'Edit user — use Status instead of Delete when access is temporary.',
          },
        ],
        manualLinks: [{ label: 'User Management → Users' }],
      },
    ],
  },
  {
    title: 'Form errors and cannot save',
    intro: 'Fix validation errors and save failures on users, roles, and organization forms.',
    items: [
      {
        question: 'Red inline errors on Create User',
        symptom: 'Required fields show red messages and Save does not complete.',
        causes: [
          'Required fields left empty.',
          'Password does not meet complexity rules.',
          'Client role selected without choosing a Client.',
        ],
        steps: [
          'Read each red inline message and fill the highlighted field.',
          'Use Generate Password if password rules are not met.',
          'If Role requires a client, select a value in the Client dropdown.',
          'Ensure email and username are unique across the system.',
        ],
        screenshots: [
          {
            src: H('faq-03-user-validation-errors.png'),
            alt: 'User form validation errors',
            caption: 'Fill required fields — read each inline error message.',
          },
          {
            src: H('um-02-create-user.png'),
            alt: 'Create user form',
            caption: 'Password field — use Generate Password or meet the rules shown.',
          },
        ],
        manualLinks: [{ label: 'User Management → Users (create form fields)' }],
      },
      {
        question: 'Cannot save a role',
        symptom: 'Create Role or Edit Role fails with a validation or permission error.',
        causes: ['No permission checkbox was selected.', 'Role name duplicates an existing role.'],
        steps: [
          'Expand permission groups and tick at least one permission.',
          'Use a unique role name.',
          'Save again — if denied, your account may lack create/edit permission for Roles.',
        ],
        screenshots: [
          {
            src: H('faq-04-role-no-permissions.png'),
            alt: 'Role form without permissions',
            caption: 'Tick at least one permission before saving.',
          },
          {
            src: H('um-04-create-role.png'),
            alt: 'Create role permissions',
            caption: 'Expand groups and assign permissions for each module.',
          },
        ],
        manualLinks: [{ label: 'User Management → Roles' }],
      },
      {
        question: 'Permission denied when saving',
        symptom: 'Form looks valid but save fails with a permission error or 403 toast.',
        causes: [
          'Your role lacks create or edit permission for that module.',
          'You opened a URL for a module not granted in Roles.',
        ],
        steps: [
          'Note the exact page (Users, Clients, Roles, etc.) where Save failed.',
          'Ask an administrator to open User Management → Roles → Edit your role and tick Create/Edit for that module.',
          'Sign out and sign in so permissions refresh.',
          'Retry Save. If it still fails, confirm Navigation includes the route for your application.',
        ],
        screenshots: [
          {
            src: H('um-04-create-role.png'),
            alt: 'Role permission matrix',
            caption: 'Admin: grant Create/Edit on the module, then ask the user to re-login.',
          },
          {
            src: H('05-after-signin.png'),
            alt: 'Dashboard after sign-in',
            caption: 'Sign out and sign in after permission changes.',
          },
        ],
        manualLinks: [
          { label: 'User Management → Roles' },
          { label: 'Settings → Navigation' },
        ],
      },
      {
        question: 'Wrong client code after save',
        symptom: 'Client was created with an incorrect code and Save on edit does not change it.',
        causes: ['Client code is immutable after the record is created.'],
        steps: [
          'Open the client in Edit mode — the Code field is disabled.',
          'Create a new client with the correct code.',
          'Soft-delete the mistaken client via Trash if it is no longer needed.',
        ],
        screenshots: [
          {
            src: H('faq-09-client-code-immutable.png'),
            alt: 'Edit client code disabled',
            caption: 'Code cannot be edited — create a new client if the code is wrong.',
          },
        ],
        manualLinks: [{ label: 'Organization → Clients' }],
      },
      {
        question: 'Store form rejects phone or contact fields',
        symptom: 'Create Store or Edit Store will not save contact information.',
        causes: [
          'Store requires either a contact phone or mobile number.',
          'Email and coordinates may be required per form validation.',
        ],
        steps: [
          'Provide Contact Phone or Mobile Number (at least one).',
          'Fill email and map coordinates if the form marks them required.',
          'Select Business Unit and other required dropdowns before Save.',
        ],
        screenshots: [
          {
            src: H('org-03-stores-create.png'),
            alt: 'Create store form',
            caption: 'Contact Phone or Mobile, email, and coordinates as required.',
          },
        ],
        manualLinks: [{ label: 'Organization → Stores' }],
      },
    ],
  },
  {
    title: 'Wrong user, role, or permissions',
    intro: 'Correct role assignments and permission mistakes after a user already exists.',
    items: [
      {
        question: 'User has the wrong role or client',
        symptom: 'Menus or actions do not match what the user should access.',
        causes: [
          'Wrong Role, Client, or Web App Access on the user record.',
          'Session still reflects the old role until re-login.',
        ],
        steps: [
          'User Management → Users → click Edit on the row (not Create New User).',
          'Correct Role, Client, and Web App Access, then Save.',
          'If the role definition itself is wrong, open Roles → Edit and adjust permissions.',
          'Ask the user to sign out and sign in so navigation refreshes.',
        ],
        screenshots: [
          {
            src: H('faq-06-users-edit-fix-role.png'),
            alt: 'Edit user role and client',
            caption: 'Step 1: Edit User — correct Role, Client, Web App Access.',
          },
          {
            src: H('um-04-create-role.png'),
            alt: 'Edit role permissions',
            caption: 'Step 3: Edit Role if the permission matrix needs changing.',
          },
          {
            src: H('05-after-signin.png'),
            alt: 'Dashboard after sign-in',
            caption: 'Step 4: User must sign out and sign in to refresh the session.',
          },
        ],
        manualLinks: [
          { label: 'User Management → Users' },
          { label: 'User Management → Roles' },
        ],
      },
      {
        question: 'Bulk role change affected the wrong users',
        symptom: 'Multiple users received the wrong role after a bulk Change role to… action.',
        causes: ['Wrong rows were selected before Apply.', 'Incorrect role chosen in the bulk toolbar.'],
        steps: [
          'Return to Users → All, select the affected rows.',
          'Use Change role to… with the correct role, then Change and confirm.',
          'Ask affected users to sign out and sign in.',
        ],
        screenshots: [
          {
            src: H('faq-07-users-bulk-role-toolbar.png'),
            alt: 'Bulk role change toolbar',
            caption: 'Select rows → Change role to… → Change → confirm.',
          },
        ],
        manualLinks: [{ label: 'User Management → Users' }],
      },
    ],
  },
  {
    title: 'Organization data mistakes',
    intro: 'Fix wrong domains, contacts, store details, or field engineer profiles.',
    items: [
      {
        question: 'Wrong client domains or contact emails',
        symptom: 'Ticket routing or notifications use incorrect domains or contacts.',
        causes: ['Typo when creating the client.', 'Contacts added to the wrong client.'],
        steps: [
          'Organization → Clients → Edit the client row.',
          'Update Email Domains and Contact Emails, then Save.',
          'Do not recreate the client unless the code itself is wrong.',
        ],
        screenshots: [
          {
            src: H('org-01-clients-create.png'),
            alt: 'Client form domains and contacts',
            caption: 'Edit Client — Email Domains and Contact Emails sections.',
          },
        ],
        manualLinks: [{ label: 'Organization → Clients' }],
      },
      {
        question: 'Wrong store or field engineer details',
        symptom: 'Store branch info or engineer profile does not match reality.',
        causes: ['Data entry error on create.', 'Bulk import with wrong template values.'],
        steps: [
          'Open Organization → Stores (or Field Engineers) and Edit the row.',
          'Correct branch code, address, phone, and linked Business Unit.',
          'Save and verify the list reflects the update.',
        ],
        screenshots: [
          {
            src: H('org-03-stores-create.png'),
            alt: 'Store form',
            caption: 'Edit Store — correct branch and contact fields.',
          },
          {
            src: H('org-06-field-engineers-create.png'),
            alt: 'Field engineer form',
            caption: 'Edit Field Engineer — profile and team assignment.',
          },
        ],
        manualLinks: [
          { label: 'Organization → Stores' },
          { label: 'Organization → Field Engineers' },
        ],
      },
      {
        question: 'Accidentally deleted an organization record',
        symptom: 'Client, store, or related record no longer appears on All.',
        causes: ['Soft-delete from row action or bulk Delete.'],
        steps: [
          'Open the org list → Trash tab.',
          'Select the row → Bulk action Restore → Apply.',
          'Avoid Delete Permanently unless recovery is not needed — Force Delete cannot be undone in-app.',
        ],
        screenshots: [
          {
            src: H('faq-02-clients-trash-restore.png'),
            alt: 'Restore org record from Trash',
            caption: 'Trash → Restore — same pattern for stores and other org lists; Force Delete cannot be undone in-app.',
          },
        ],
        manualLinks: [{ label: 'Organization → Clients' }],
      },
    ],
  },
  {
    title: 'Asset Catalog',
    intro:
      'Per-client catalog (categories, brands, models, specs) opened from Organization → Clients → Manage Asset Catalog.',
    items: [
      {
        question: 'Cannot find Asset Catalog in the sidebar',
        symptom: 'There is no Asset Catalog group under the main Auth menu.',
        causes: [
          'Asset Catalog is not a global sidebar module.',
          'It is opened per client from Organization → Clients.',
        ],
        steps: [
          'Open Organization → Clients.',
          'Use the row Actions menu → Manage Asset Catalog (only when the client database is provisioned).',
          'Use the left Catalog menu inside that workspace (Overview, Categories, Brands, specs, Tags).',
        ],
        screenshots: [
          {
            src: H('ac-01-clients-manage-action.png'),
            alt: 'Manage Asset Catalog on Clients list',
            caption: 'Clients → Actions → Manage Asset Catalog.',
          },
          {
            src: H('ac-02-overview.png'),
            alt: 'Catalog workspace Overview',
            caption: 'Workspace: client badge + left Catalog sub-navigation + Overview.',
          },
        ],
        manualLinks: [
          { label: 'Organization → Clients' },
          { label: 'Asset Catalog → Open Manage Asset Catalog' },
        ],
      },
      {
        question: 'Manage Asset Catalog is missing on a client',
        symptom: 'Row Actions does not show Manage Asset Catalog.',
        causes: [
          'Client database is not provisioned yet.',
          'You lack permission to view/edit that client.',
        ],
        steps: [
          'On the Clients list, check the Database column.',
          'If empty, run Create Database for that client and wait until provisioning completes.',
          'Refresh the list — Manage Asset Catalog appears for clients with a completed database.',
        ],
        screenshots: [
          {
            src: H('ac-01-clients-manage-action.png'),
            alt: 'Clients list Database and Actions',
            caption: 'Manage Asset Catalog requires a provisioned Database (e.g. sima_demo).',
          },
        ],
        manualLinks: [{ label: 'Asset Catalog → Open Manage Asset Catalog' }],
      },
      {
        question: 'Wrong client catalog or empty lists',
        symptom: 'Categories/brands look empty or belong to another tenant.',
        causes: [
          'Catalog is always filtered to the client you opened from Manage Asset Catalog.',
          'That client DB has no master data yet.',
        ],
        steps: [
          'Confirm the client badge in the Manage Asset Catalog header.',
          'Use Back to Clients, then open Manage Asset Catalog on the correct client row.',
          'On Overview, follow Categories → Functions → Brands → Models → Specs → Tags.',
        ],
        screenshots: [
          {
            src: H('ac-02-overview.png'),
            alt: 'Client badge on catalog Overview',
            caption: 'Header badge shows which client DB you are editing.',
          },
          {
            src: H('ac-03-categories-list.png'),
            alt: 'Categories for the selected client',
            caption: 'Left Catalog menu stays visible while you manage Categories for that client.',
          },
        ],
        manualLinks: [
          { label: 'Asset Catalog → Workspace layout' },
          { label: 'Asset Catalog → Categories and Functions' },
        ],
      },
      {
        question: 'CSV import fails on Functions or Models',
        symptom: 'Row errors say Parent category / Brand / Category not found.',
        causes: [
          'Import order was wrong — Functions need Categories; Models need Brands and Categories first.',
          'Parent names/slugs in the CSV do not match rows already in this client catalog.',
        ],
        steps: [
          'Download the Template on each list page (comment lines document required order).',
          'Import Categories, then Functions, then Brands, then Models; Specs and Tags can follow in any order.',
          'Fix missing parents or correct name/slug spelling, then re-import (duplicates upsert by natural key).',
        ],
        manualLinks: [{ label: 'Asset Catalog → Categories and Functions' }],
      },
      {
        question: 'Cannot see Template / Import / Export on Asset Catalog',
        symptom: 'Only Create appears on Categories or other catalog lists.',
        causes: [
          'Your role lacks can_import or can_export for that navigation entry.',
          'Template/Import/Export are per catalog module inside Manage Asset Catalog (not a single ZIP of the whole catalog).',
        ],
        steps: [
          'Ask an admin to grant import/export on the Asset Catalog (or matching) permissions.',
          'Open the specific list (e.g. Categories) — icons match Clients: template download, import CSV, export CSV.',
        ],
        manualLinks: [{ label: 'Asset Catalog → Categories and Functions' }],
      },
    ],
  },
  {
    title: 'Bulk actions and CSV import mistakes',
    intro: 'Recover from accidental bulk delete, mass password reset, or bad CSV imports.',
    items: [
      {
        question: 'Accidental bulk delete',
        symptom: 'Many rows vanished after Bulk actions → Delete → Apply.',
        causes: ['Wrong rows selected.', 'Delete chosen instead of another bulk action.'],
        steps: [
          'Immediately open Trash — do not force-delete.',
          'Select affected rows → Bulk action Restore → Apply.',
          'Review the confirmation dialog before any future bulk delete.',
        ],
        screenshots: [
          {
            src: H('faq-05-users-bulk-delete-confirm.png'),
            alt: 'Bulk delete confirmation',
            caption: 'Before Apply: read the confirmation — Delete moves rows to Trash.',
          },
          {
            src: H('faq-01-users-trash-restore.png'),
            alt: 'Restore bulk deleted users',
            caption: 'Trash → Restore as soon as possible; Delete Permanently cannot be undone in-app.',
          },
        ],
        manualLinks: [{ label: 'User Management → Users' }],
      },
      {
        question: 'Mass password reset sent by mistake',
        symptom: 'Many users received password reset emails after bulk Send password reset.',
        causes: ['Bulk action applied to too many selected rows.'],
        steps: [
          'Stop further bulk password actions until verified.',
          'Inform affected users the email may be ignored if not requested.',
          'Verify Settings → Email Settings if messages did not send (see Email section).',
        ],
        screenshots: [
          {
            src: H('set-03-email-settings.png'),
            alt: 'Email settings',
            caption: 'Confirm SMTP and From address if reset mail failed to send.',
          },
        ],
        manualLinks: [{ label: 'Settings → Email Settings' }],
      },
      {
        question: 'CSV import created wrong or duplicate users',
        symptom: 'Import summary shows errors, partial rows, or unexpected new accounts.',
        causes: [
          'Template columns do not match the file.',
          'Duplicate emails or logins in the CSV.',
        ],
        steps: [
          'Read the import toast or summary message for row-level errors.',
          'Use the toolbar icons: Download Template, then Import after fixing the CSV offline.',
          'Trash incorrect rows and re-import after fixing the file.',
        ],
        screenshots: [
          {
            src: H('faq-08-import-template-toolbar.png'),
            alt: 'Import export toolbar',
            caption: 'Step 2: Top-right Export / Download Template / Import icons (highlighted) next to Create New User.',
          },
        ],
        manualLinks: [{ label: 'User Management → Users (bulk / import)' }],
      },
    ],
  },
  {
    title: 'Email not delivering',
    intro: 'Password reset, invite, and notification mail never arrives.',
    items: [
      {
        question: 'Reset or notification email never arrives',
        symptom: 'User or admin triggered email but nothing appears in the inbox.',
        causes: [
          'Message in spam or junk folder.',
          'SMTP or provider misconfiguration.',
          'Email template inactive for the event type.',
        ],
        steps: [
          'Ask the recipient to check spam/junk and wait a few minutes.',
          'Settings → Email Settings — verify SMTP host, port, encryption, and From address.',
          'Settings → Email Templates — confirm the template for the event is Active.',
          'Send a test to a known-good address before retrying bulk actions.',
        ],
        screenshots: [
          {
            src: H('02-forgot-password.png'),
            alt: 'Forgot password flow',
            caption: 'Step 1: Reset is triggered from login — user should check spam.',
          },
          {
            src: H('set-03-email-settings.png'),
            alt: 'Email settings',
            caption: 'Step 2: Email Settings — SMTP and From address.',
          },
          {
            src: H('set-04-email-templates-list.png'),
            alt: 'Email templates list',
            caption: 'Step 3: Email Templates — Active template for the event.',
          },
        ],
        manualLinks: [
          { label: 'Settings → Email Settings' },
          { label: 'Settings → Email Templates' },
        ],
      },
    ],
  },
  {
    title: 'Missing menus or bounce to dashboard',
    intro: 'Sidebar items missing or a deep link returns to the dashboard.',
    items: [
      {
        question: 'Signed in but expected menu item is missing',
        symptom: 'Sidebar does not show a module the user should access.',
        causes: [
          'Role lacks permission for the module.',
          'Navigation entry hidden or tied to wrong application.',
          'Session not refreshed after permission change.',
        ],
        steps: [
          'Confirm the user signed in to the correct app (Auth vs Support).',
          'User Management → Roles → Edit — tick permissions for the missing module.',
          'Settings → Navigation — verify Show in menu and Application (auth).',
          'User signs out and signs in again.',
        ],
        screenshots: [
          {
            src: H('05-after-signin.png'),
            alt: 'Dashboard with sidebar',
            caption: 'Step 1: Confirm sidebar after sign-in to the correct app.',
          },
          {
            src: H('um-04-create-role.png'),
            alt: 'Role permissions',
            caption: 'Step 2: Role permission matrix for the missing module.',
          },
          {
            src: H('set-07-navigation-create.png'),
            alt: 'Navigation entry',
            caption: 'Step 3: Navigation — route, Show in menu, Application.',
          },
        ],
        manualLinks: [
          { label: 'User Management → Roles' },
          { label: 'Settings → Navigation' },
        ],
      },
      {
        question: 'Page opens then returns to dashboard',
        symptom: 'A bookmarked or shared URL loads briefly then redirects home.',
        causes: [
          'Route not registered in navigation for this user.',
          'Permission denied for the path.',
        ],
        steps: [
          'Open the same feature from the sidebar instead of a bookmark.',
          'If sidebar also missing, fix role permissions and navigation (steps above).',
          'Report the exact URL to an administrator if the sidebar link works but the URL does not.',
        ],
        screenshots: [
          {
            src: H('05-after-signin.png'),
            alt: 'Use sidebar navigation',
            caption: 'Prefer sidebar links — bookmarks may point at denied routes.',
          },
        ],
        manualLinks: [{ label: 'Settings → Navigation' }],
      },
    ],
  },
  {
    title: 'Backup and Restore (last resort)',
    intro: 'For mass data loss or corruption only — not for single deleted rows.',
    items: [
      {
        question: 'Mass data loss or corruption',
        symptom: 'Many records force-deleted or database corruption; Trash cannot recover.',
        causes: [
          'Delete Permanently used on Trash rows.',
          'Import or external process corrupted data.',
        ],
        steps: [
          'For single deleted rows, always try Trash → Restore first.',
          'Only if mass loss — Settings → Backup & Restore → restore from a known-good backup.',
          'Coordinate downtime; restore overwrites live data — get approval before proceeding.',
        ],
        screenshots: [
          {
            src: H('faq-01-users-trash-restore.png'),
            alt: 'Try Trash first',
            caption: 'Step 1: Trash → Restore for single-row mistakes.',
          },
          {
            src: H('set-08-backup-list.png'),
            alt: 'Backup and restore',
            caption: 'Step 2: Backup & Restore — last resort; overwrites current data.',
          },
        ],
        manualLinks: [{ label: 'Settings → Backup & Restore' }],
      },
    ],
  },
]

export default function FaqPage() {
  const { textSecondary, border } = useThemeColors()

  return (
    <BasePageLayout
      mainBoxProps={{
        p: 4,
        h: 'full',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <VStack align="stretch" spacing={6} w="full" maxW="1200px">
        <Box>
          <Heading size="lg" mb={2}>
            FAQ — Troubleshooting
          </Heading>
          <Text color={textSecondary}>
            Symptom → cause → fix steps for when actions fail or wrong data was created. For field-by-field
            how-to guidance, see the{' '}
            <ChakraLink as={RouterLink} to="/user-manual" color="blue.500">
              User Manual
            </ChakraLink>
            .
          </Text>
        </Box>

        {faqCategories.map((category) => (
          <Box key={category.title}>
            <Heading size="md" mb={category.intro ? 2 : 4}>
              {category.title}
            </Heading>
            {category.intro && (
              <Text color={textSecondary} mb={4} fontSize="sm">
                {category.intro}
              </Text>
            )}
            <Accordion allowMultiple reduceMotion>
              {category.items.map((item) => (
                <AccordionItem key={item.question} borderColor={border}>
                  <h3>
                    <AccordionButton py={3} px={2}>
                      <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                        {item.question}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h3>
                  <AccordionPanel pb={4} px={2}>
                    <TroubleshootStep
                      symptom={item.symptom}
                      causes={item.causes}
                      steps={item.steps}
                      screenshots={item.screenshots}
                      manualLinks={item.manualLinks}
                      border={border}
                      textSecondary={textSecondary}
                    />
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
            <Divider mt={6} borderColor={border} />
          </Box>
        ))}
      </VStack>
    </BasePageLayout>
  )
}
